import dragon from "../../assets/static/images/holy-wheel/rewards/custom/dragon-bundle-wheel.webp";
import dragonOverview from "../../assets/static/images/holy-wheel/rewards/custom/dragon-bundle-overview.webp";
import tunic from "../../assets/static/images/holy-wheel/rewards/custom/royal-gold-tunic-wheel.webp";
import tunicOverview from "../../assets/static/images/holy-wheel/rewards/custom/royal-gold-tunic-overview.webp";
import sunglasses from "../../assets/static/images/holy-wheel/rewards/custom/golden-black-sunglasses-wheel.webp";
import sunglassesOverview from "../../assets/static/images/holy-wheel/rewards/custom/golden-black-sunglasses-overview.webp";
import type {
    WheelSnapshotV2,
    WheelWalletV1,
    WheelPublicOutcomeV2,
} from "@bridge-applications/pb-types";
import type { HolyWheelPrize } from "./holyWheelPrizes";

export function wheelText(
    text: Record<string, string>,
    locale: string,
): string {
    return text[locale] ?? text[locale.split("-")[0] ?? "en"] ?? text.en ?? "";
}
export function newestWheelWallet(
    current: WheelWalletV1,
    incoming: WheelWalletV1,
): WheelWalletV1 {
    const a = BigInt(current.version),
        b = BigInt(incoming.version);
    return b > a ||
        (b === a &&
            Date.parse(incoming.observedAt) >= Date.parse(current.observedAt))
        ? incoming
        : current;
}
export function wheelIdentity(wheel: WheelSnapshotV2) {
    return {
        wheelId: wheel.wheelId,
        campaignId: wheel.campaignId,
        variantId: wheel.variantId,
        revisionId: wheel.revisionId,
        decisionId: wheel.decisionId,
        betPolicyId: wheel.betPolicyId,
    };
}
export interface WheelLauncher {
    wheelCode: string;
    presentation: WheelSnapshotV2["content"]["presentation"];
    availableSpins: string;
}
export function wheelLauncher(wheel: WheelSnapshotV2): WheelLauncher {
    return {
        wheelCode: wheel.wheelCode,
        presentation: wheel.content.presentation,
        availableSpins: wheel.wallet.availableSpins,
    };
}

type WheelPrizeDisplay = Pick<WheelPublicOutcomeV2, "presentation"> & {
    rewards: readonly Pick<
        WheelPublicOutcomeV2["rewards"][number],
        "kind" | "amount"
    >[];
};
export function wheelPrize(
    outcome: WheelPrizeDisplay,
    index: number,
    locale: string,
): HolyWheelPrize {
    const base = {
        overviewListPriority: outcome.presentation.overviewOrder ?? index,
        label: wheelText(outcome.presentation.label, locale),
        imageUrl: outcome.presentation.imageUrl,
        wheelImageUrl: outcome.presentation.wheelImageUrl,
        overviewImageUrl: outcome.presentation.overviewImageUrl,
        tagline: outcome.presentation.tagline
            ? wheelText(outcome.presentation.tagline, locale)
            : undefined,
        description: outcome.presentation.description
            ? wheelText(outcome.presentation.description, locale)
            : undefined,
    };
    const reward = outcome.rewards[0];
    if (!reward) throw new Error("Wheel outcome has no reward");
    if (reward.kind === "SM_ENERGY" || reward.kind === "CURRENCY")
        return {
            ...base,
            kind: reward.kind === "SM_ENERGY" ? "energy" : "beach-coins",
            amount: reward.amount,
        };
    const kind = outcome.presentation.artwork;
    if (kind === "dragon-bundle")
        return {
            ...base,
            imageUrl: base.imageUrl ?? dragon,
            overviewImageUrl:
                base.overviewImageUrl ?? base.imageUrl ?? dragonOverview,
            kind: "angel-set",
            bundleSize: 3,
        };
    if (kind === "royal-gold-tunic")
        return {
            ...base,
            imageUrl: base.imageUrl ?? tunic,
            overviewImageUrl:
                base.overviewImageUrl ?? base.imageUrl ?? tunicOverview,
            kind: "golden-shirt",
        };
    if (kind === "golden-black-sunglasses")
        return {
            ...base,
            imageUrl: base.imageUrl ?? sunglasses,
            overviewImageUrl:
                base.overviewImageUrl ?? base.imageUrl ?? sunglassesOverview,
            kind: "golden-sunglasses",
        };
    if (kind === "beach-set" || kind === "angel-set") {
        const size = outcome.rewards.length;
        if (
            size === 2 ||
            size === 3 ||
            size === 4 ||
            size === 5 ||
            size === 6 ||
            size === 7 ||
            size === 8
        )
            return { ...base, kind, bundleSize: size };
    }
    return {
        ...base,
        kind: kind === "golden-sunglasses" ? kind : "golden-shirt",
    };
}

/** Use a bounded number only for i18next's plural category; interpolate the exact
 * decimal text. Never round a wallet counter for display or settlement. */
export function wheelCountOptions(value: string | number) {
    const count = BigInt(value);
    const pluralCount =
        count <= 1000000n ? count : 1000000n + (count % 1000000n);
    return { count: Number(pluralCount), replace: { count: String(value) } };
}
